/**
 * Executive Home — chapter "EXECUTIVE HOME" of the Executive Experience ask.
 *
 * Purpose: answer, in 60 seconds, the six executive questions:
 *   How large is our estate? What is it costing? What needs attention?
 *   What risks? What value is Suntronix delivering? What is coming next?
 *
 * Zero new backend — composes the existing executive dashboard, warranty summary,
 * refresh engine, and lifecycle activity endpoints already used elsewhere.
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
  Cpu, Truck, ShieldAlert, DollarSign, Leaf, TriangleAlert,
  Recycle, Globe2, TrendingUp, ArrowRight, Sparkles, CheckCircle2,
  ScanLine, Cable, ShieldCheck,
} from "lucide-react";

function greeting() {
  const h = new Date().getHours();
  if (h < 5) return "Good night";
  if (h < 12) return "Good morning";
  if (h < 17) return "Good afternoon";
  return "Good evening";
}

export default function ExecutiveHome() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [exec, setExec] = useState(null);
  const [warranty, setWarranty] = useState(null);
  const [refresh, setRefresh] = useState(null);
  const [beacon, setBeacon] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancel = false;
    Promise.all([
      api.get("/dashboard/executive"),
      api.get("/warranty/summary").catch(() => ({ data: {} })),
      api.get("/refresh-engine/summary").catch(() => ({ data: {} })),
      api.get("/one-connect/beacon/compliance").catch(() => ({ data: null })),
    ]).then(([e, w, r, b]) => {
      if (cancel) return;
      setExec(e.data);
      setWarranty(w.data);
      setRefresh(r.data);
      setBeacon(b.data);
    }).finally(() => !cancel && setLoading(false));
    return () => { cancel = true; };
  }, []);

  const firstName = (user?.name || "there").split(" ")[0];
  const tenant = exec?.tenant_name || "your organisation";
  const kpi = exec?.kpi || {};
  const estate = exec?.estate || {};
  const activity = exec?.lifecycle_activity || {};

  const byCountry = estate.by_country || [];
  const under = warranty?.under_warranty ?? 0;
  const outOfW = warranty?.out_of_warranty ?? 0;
  const exp90 = (warranty?.expiring_30 ?? 0) + (warranty?.expiring_60 ?? 0) + (warranty?.expiring_90 ?? 0);
  const refresh12m = (refresh?.buckets || []).find((b) => b.window === "12m")?.count || 0;
  const refresh24m = (refresh?.buckets || []).find((b) => b.window === "24m")?.count || 0;

  const totalAssets = exec?.total_assets || 0;
  const countries = exec?.countries || 0;

  return (
    <div className="space-y-8 max-w-[1400px]" data-testid="executive-home">
      {/* HERO */}
      <div className="flex flex-col gap-2">
        <div className="flex items-baseline gap-2 flex-wrap">
          <h1 className="text-3xl font-heading font-semibold text-slate-900 tracking-tighter">{greeting()}, {firstName}</h1>
          <span className="text-slate-300">·</span>
          <span className="text-lg text-slate-500">{tenant}</span>
          <span className="ml-2 text-[10px] font-semibold uppercase tracking-widest rounded-full bg-slate-900 text-amber-400 px-2 py-0.5">Executive view</span>
        </div>
        <div className="text-sm text-slate-500">Everything you need to answer <em>where we are, what risks exist, what value we are getting and what is coming next</em> — in one screen.</div>
      </div>

      {/* Fleet Health Beacon — MDM compliance rollup from ONE CONNECT */}
      {beacon && beacon.devices_with_evidence > 0 && (
        <button
          onClick={() => navigate("/admin/one-connect")}
          data-testid="exec-fleet-beacon"
          className={`w-full text-left border rounded-md px-4 py-3 flex items-center gap-3 transition-colors ${
            beacon.devices_non_compliant > 0
              ? "bg-rose-50 border-rose-200 hover:bg-rose-100"
              : "bg-emerald-50 border-emerald-200 hover:bg-emerald-100"
          }`}
        >
          <span className={`grid place-items-center h-9 w-9 rounded-md ${
            beacon.devices_non_compliant > 0
              ? "bg-rose-500/15 text-rose-700"
              : "bg-emerald-500/15 text-emerald-700"
          }`}>
            {beacon.devices_non_compliant > 0
              ? <ShieldAlert className="h-4 w-4" strokeWidth={1.6} />
              : <ShieldCheck className="h-4 w-4" strokeWidth={1.6} />}
          </span>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-widest text-slate-500">Fleet health · ONE CONNECT</div>
            <div className="text-sm font-medium text-slate-900">
              {beacon.devices_non_compliant > 0
                ? `${beacon.devices_non_compliant.toLocaleString()} devices out of compliance across MDM`
                : `${beacon.devices_with_evidence.toLocaleString()} devices in sync with MDM · no drift`}
            </div>
            <div className="text-[11px] text-slate-500 mt-0.5 flex items-center gap-2 flex-wrap">
              {beacon.connectors?.map((c) => (
                <span key={c.connector_id} className="inline-flex items-center gap-1">
                  <Cable className="h-3 w-3 text-slate-400" strokeWidth={1.6} />
                  <span className="font-mono">{c.connector_id}</span>
                  <span className="tabular-nums">{c.non_compliant}/{c.total}</span>
                </span>
              ))}
              {beacon.last_sync_at && (
                <span className="ml-auto">Last sync {new Date(beacon.last_sync_at).toLocaleString()}</span>
              )}
            </div>
          </div>
          <ArrowRight className="h-4 w-4 text-slate-400" strokeWidth={1.5} />
        </button>
      )}

      {/* 1. TECHNOLOGY ESTATE */}
      <SectionHeader title="Technology estate" caption="How large is our estate?" />
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        <ExecTile label="Total assets"      value={totalAssets} icon={Cpu} onClick={() => navigate("/assets")} testId="exec-total-assets" />
        <ExecTile label="Employees covered" value={exec?.employees || 0} icon={Cpu} onClick={() => navigate("/employees")} />
        <ExecTile label="Countries"         value={countries} icon={Globe2} />
        <ExecTile label="Being delivered"   value={kpi.in_transit || 0} icon={Truck} tone="sky" onClick={() => navigate("/shipments")} />
      </div>

      {/* Quick actions — scan asset for field ops */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
        <button
          onClick={() => navigate("/scan-asset")}
          className="text-left border rounded-md p-4 bg-slate-900 text-white hover:bg-slate-800 transition-colors flex items-center gap-3"
          data-testid="exec-scan-asset">
          <span className="grid place-items-center h-9 w-9 rounded-md bg-amber-500/20 text-amber-300">
            <ScanLine className="h-4 w-4" />
          </span>
          <span>
            <div className="text-[11px] uppercase tracking-widest text-slate-300">Scan asset</div>
            <div className="text-sm font-medium">Camera → serial → Passport</div>
          </span>
        </button>
        <button
          onClick={() => navigate("/inventory")}
          className="text-left border rounded-md p-4 hover:bg-slate-50 transition-colors flex items-center gap-3"
          data-testid="exec-inventory-quick">
          <span className="grid place-items-center h-9 w-9 rounded-md bg-slate-100 text-slate-700">
            <Cpu className="h-4 w-4" />
          </span>
          <span>
            <div className="text-[11px] uppercase tracking-widest text-slate-500">Inventory</div>
            <div className="text-sm font-medium">Search the Golden Asset Record</div>
          </span>
        </button>
        <button
          onClick={() => navigate("/approvals-inbox")}
          className="text-left border rounded-md p-4 hover:bg-slate-50 transition-colors flex items-center gap-3"
          data-testid="exec-approvals-quick">
          <span className="grid place-items-center h-9 w-9 rounded-md bg-slate-100 text-slate-700">
            <Truck className="h-4 w-4" />
          </span>
          <span>
            <div className="text-[11px] uppercase tracking-widest text-slate-500">Approvals</div>
            <div className="text-sm font-medium">Decide pending requests</div>
          </span>
        </button>
      </div>

      {/* 2. RISK / ESTATE HEALTH */}
      <SectionHeader title="Estate health" caption="No single black-box score — the dimensions that matter, individually." />
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-2">
        <HealthTile
          label="Warranty"
          value={outOfW + exp90}
          state={outOfW + exp90 > 0 ? "attention" : "healthy"}
          note={`${outOfW.toLocaleString()} out of warranty · ${exp90.toLocaleString()} expiring in 90 days`}
          onClick={() => navigate("/warranty")}
          testId="exec-warranty"
        />
        <HealthTile
          label="Refresh"
          value={refresh12m}
          state={refresh12m > 0 ? "attention" : "healthy"}
          note={`${refresh12m.toLocaleString()} due next 12 months · ${refresh24m.toLocaleString()} in 24 months`}
          onClick={() => navigate("/refresh-engine")}
          testId="exec-refresh"
        />
        <HealthTile
          label="Recoveries"
          value={kpi.recovery || 0}
          state={(kpi.recovery || 0) > 0 ? "attention" : "healthy"}
          note="Outstanding devices to be recovered"
          onClick={() => navigate("/pickups")}
        />
        <HealthTile
          label="Service"
          value={kpi.repair || 0}
          state={(kpi.repair || 0) > 0 ? "info" : "healthy"}
          note="Devices currently under repair"
          onClick={() => navigate("/service")}
        />
      </div>

      {/* 3. VALUE DELIVERED */}
      <SectionHeader title="Value delivered by Suntronix" caption="Every number below is grounded in actual recorded transactions." />
      <div className="bg-slate-900 text-slate-100 rounded-md p-6 grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
        <ValueTile label="Devices managed"    value={totalAssets} />
        <ValueTile label="Deployments"        value={activity.deployed || 0} />
        <ValueTile label="Recoveries"         value={activity.recovered || 0} />
        <ValueTile label="Redeployed"         value={activity.reused || 0} />
        <ValueTile label="Countries supported" value={countries} />
        <ValueTile label="Disposed"           value={activity.disposed || 0} />
      </div>

      {/* 4. GLOBAL DISTRIBUTION */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div>
          <SectionHeader title="Global distribution" caption="Where our estate lives — click a country to drill down." small />
          <div className="bg-white border border-slate-200 rounded-md">
            {loading ? (
              <div className="p-6 text-sm text-slate-500">Loading…</div>
            ) : byCountry.length === 0 ? (
              <div className="p-6 text-sm text-slate-500">No country data yet.</div>
            ) : (
              <ul className="divide-y divide-slate-100">
                {byCountry.slice(0, 8).map((c) => {
                  const pct = totalAssets ? Math.round((c.count / totalAssets) * 100) : 0;
                  return (
                    <li key={c.key} className="px-4 py-2.5 flex items-center gap-3">
                      <Globe2 className="h-3.5 w-3.5 text-slate-400 shrink-0" strokeWidth={1.6} />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-slate-800 font-medium">{c.key}</span>
                          <span className="tabular-nums text-slate-500">{c.count.toLocaleString()} · {pct}%</span>
                        </div>
                        <div className="h-1 mt-0.5 rounded-full bg-slate-100 overflow-hidden">
                          <div className="h-full bg-slate-900" style={{ width: `${Math.max(2, pct)}%` }} />
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>

        {/* 5. OPERATIONS SLA + FINANCIAL */}
        <div>
          <SectionHeader title="Operations" caption="How Suntronix is delivering for your team, this quarter." small />
          <div className="bg-white border border-slate-200 rounded-md p-4 grid grid-cols-2 gap-4">
            <MiniStat label="Deployments (90d)"  value={activity.deployed || 0} />
            <MiniStat label="Recoveries (90d)"   value={activity.recovered || 0} />
            <MiniStat label="Disposed (90d)"     value={activity.disposed || 0} />
            <MiniStat label="Reused / redeployed" value={activity.reused || 0} />
          </div>

          <SectionHeader title="Financial · estimated" caption="Only shown when actual pricing data exists on records." small />
          <div className="bg-white border border-slate-200 rounded-md p-4 grid grid-cols-2 gap-4">
            <MiniStat label="Estate purchase value" value={exec?.estate_value ? `USD ${Math.round(exec.estate_value).toLocaleString()}` : "—"} raw />
            <MiniStat label="Buyback recovered" value={exec?.buyback_recovered ? `USD ${Math.round(exec.buyback_recovered).toLocaleString()}` : "—"} raw />
          </div>
        </div>
      </div>

      {/* 6. WHAT NEEDS ATTENTION */}
      <SectionHeader title="What needs attention" caption="Only the executive-relevant items. Operational detail lives inside each queue." />
      {(exec?.attention || []).length === 0 ? (
        <div className="bg-white border border-emerald-200 rounded-md p-8 flex items-start gap-4">
          <CheckCircle2 className="h-6 w-6 text-emerald-600 shrink-0 mt-0.5" strokeWidth={1.6} />
          <div>
            <div className="text-lg font-heading font-semibold text-slate-900">The estate is healthy.</div>
            <div className="text-sm text-slate-600 mt-1">No item currently requires executive attention. Suntronix continues to run daily operations on your behalf.</div>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-2">
          {(exec?.attention || []).slice(0, 6).map((a) => (
            <button
              key={a.kind + a.label}
              onClick={() => navigate(a.link || "/exceptions")}
              className={`text-left border rounded-md px-4 py-3 flex items-center gap-3 transition-colors ${severityClass(a.severity)}`}
            >
              <TriangleAlert className="h-4 w-4 shrink-0" strokeWidth={1.5} />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-slate-900">{a.label}</div>
                <div className="text-[10px] uppercase tracking-widest text-slate-500 mt-0.5">{a.severity}</div>
              </div>
              <div className="text-2xl font-heading font-bold tabular-nums text-slate-900">{a.count}</div>
              <ArrowRight className="h-4 w-4 text-slate-400" strokeWidth={1.5} />
            </button>
          ))}
        </div>
      )}

      {/* 7. FOOTER — Board report */}
      <div className="bg-slate-50 border border-slate-200 rounded-md p-5 flex items-center gap-4 no-print">
        <div className="h-10 w-10 rounded-md bg-slate-900 grid place-items-center text-amber-400 shrink-0"><Sparkles className="h-5 w-5" strokeWidth={1.5} /></div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold text-slate-900">Board / QBR report</div>
          <div className="text-xs text-slate-500 mt-0.5">Print or export this page as a branded PDF — use your browser's Save as PDF option in the print dialog.</div>
        </div>
        <button
          className="text-xs px-3 py-1.5 rounded-md bg-slate-900 text-white hover:bg-slate-800 transition-colors"
          onClick={async () => {
            try {
              const res = await api.get("/reports/executive-pdf", { responseType: "blob" });
              const url = URL.createObjectURL(res.data);
              const a = document.createElement("a");
              a.href = url;
              a.download = `SuntronixONE_ExecutiveReport_${new Date().toISOString().slice(0, 10)}.pdf`;
              document.body.appendChild(a);
              a.click();
              a.remove();
              URL.revokeObjectURL(url);
            } catch {
              // If server-side PDF is unavailable, fall back to browser print.
              window.print();
            }
          }}
          data-testid="exec-print-report"
        >
          Download PDF report →
        </button>
      </div>
    </div>
  );
}

/* ---------- helpers ---------- */
function SectionHeader({ title, caption, small }) {
  return (
    <div className={small ? "mb-2" : "mb-3"}>
      <h2 className={small ? "text-sm font-heading font-semibold text-slate-900" : "text-lg font-heading font-semibold text-slate-900 tracking-tight"}>{title}</h2>
      {caption && <div className={small ? "text-[11px] text-slate-500" : "text-xs text-slate-500"}>{caption}</div>}
    </div>
  );
}

function ExecTile({ label, value, icon: Icon, tone = "slate", onClick, testId }) {
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      className="text-left bg-white border border-slate-200 rounded-md p-4 hover:border-slate-300 hover:-translate-y-px hover:shadow-sm transition-[transform,box-shadow,border-color]"
    >
      <div className="flex items-start justify-between">
        <div className="text-[10px] font-semibold tracking-[0.08em] uppercase text-slate-500">{label}</div>
        <Icon className="h-3.5 w-3.5 text-slate-300" strokeWidth={1.6} />
      </div>
      <div className={`mt-2 text-2xl font-heading font-bold tabular-nums ${tone === "sky" ? "text-sky-800" : "text-slate-900"}`}>
        {value.toLocaleString()}
      </div>
    </button>
  );
}

function HealthTile({ label, value, state, note, onClick, testId }) {
  const style = {
    healthy:   { bar: "bg-emerald-100 text-emerald-800 border-emerald-200", dot: "bg-emerald-500", stateLabel: "Healthy" },
    info:      { bar: "bg-sky-50 text-sky-800 border-sky-200", dot: "bg-sky-500", stateLabel: "In flight" },
    attention: { bar: "bg-amber-50 text-amber-800 border-amber-200", dot: "bg-amber-500", stateLabel: "Attention" },
  }[state] || { bar: "bg-slate-50 text-slate-700 border-slate-200", dot: "bg-slate-400", stateLabel: "—" };
  return (
    <button onClick={onClick} data-testid={testId} className={`text-left border rounded-md p-4 hover:brightness-95 transition-[filter] ${style.bar}`}>
      <div className="flex items-center gap-2">
        <span className={`h-2 w-2 rounded-full ${style.dot}`} />
        <div className="text-[10px] font-semibold tracking-[0.08em] uppercase text-slate-600">{label}</div>
        <div className="ml-auto text-[10px] font-semibold uppercase tracking-widest text-slate-500">{style.stateLabel}</div>
      </div>
      <div className="mt-2 text-2xl font-heading font-bold tabular-nums text-slate-900">{value.toLocaleString()}</div>
      <div className="text-[11px] text-slate-600 mt-1 leading-relaxed">{note}</div>
    </button>
  );
}

function ValueTile({ label, value }) {
  return (
    <div>
      <div className="text-[10px] font-semibold tracking-[0.08em] uppercase text-amber-400">{label}</div>
      <div className="mt-1 text-2xl font-heading font-bold tabular-nums text-white">{(value || 0).toLocaleString()}</div>
    </div>
  );
}

function MiniStat({ label, value, raw }) {
  return (
    <div>
      <div className="text-[10px] font-semibold tracking-[0.08em] uppercase text-slate-500">{label}</div>
      <div className="mt-1 text-xl font-heading font-bold tabular-nums text-slate-900">{raw ? value : (value || 0).toLocaleString()}</div>
    </div>
  );
}

function severityClass(sev) {
  return {
    critical: "bg-rose-50 border-rose-200 hover:bg-rose-100",
    high:     "bg-amber-50 border-amber-200 hover:bg-amber-100",
    medium:   "bg-sky-50 border-sky-200 hover:bg-sky-100",
    low:      "bg-slate-50 border-slate-200 hover:bg-slate-100",
  }[sev] || "bg-slate-50 border-slate-200";
}
